import { useCallback, useContext, useMemo, useState } from 'react';
import { useQuery, QueryResult } from '@apollo/client';
import {
  UseAdminUsers,
  UseAdminUsersVars,
  AdminUsersVars,
  isAdminUsersSorting,
  DEFAULT_PAGE,
  ADMIN_USERS_PAGE_SIZE,
  AdminUsersSearchParams,
  ApiAdminUsersVars,
  DEFAULT_ADMIN_USERS_VARS,
  ApiAdminUsersResponse
} from './types';
import { AdminUsersData } from '.';
import { GET_ALL_USERS, GET_ACTIVE_USERS_INFO } from './queries';
import { AdminUsersContext } from './context';
import { isString, updateQueryParams, useQueryParams } from 'src/helpers';
import { isRoles } from 'src/models/users/Roles/types';

export function useAdminUsersVars(): UseAdminUsersVars {
  const ctx = useContext(AdminUsersContext);
  if (!ctx) {
    throw new Error('not in AdminUsersContext provider');
  }

  return ctx;
}
export function useAdminUsersCtx(): UseAdminUsersVars {
  const defaultArgs = DEFAULT_ADMIN_USERS_VARS;
  const page = DEFAULT_PAGE;
  const pageSize = ADMIN_USERS_PAGE_SIZE;

  const qp = useQueryParams();

  const [args, setArgs] = useState<AdminUsersVars>(() => {
    const rolesSearchParams = qp[AdminUsersSearchParams.roles];
    return {
      sortBy: isAdminUsersSorting(qp[AdminUsersSearchParams.sortBy]) ? qp[AdminUsersSearchParams.sortBy] : undefined,
      email: isString(qp[AdminUsersSearchParams.email]) ? qp[AdminUsersSearchParams.email] : undefined,
      companyId: isString(qp[AdminUsersSearchParams.companyId]) ? qp[AdminUsersSearchParams.companyId] : undefined,
      roles: isRoles(rolesSearchParams) && !!rolesSearchParams.length ? rolesSearchParams : undefined
    };
  });

  const vars: ApiAdminUsersVars = useMemo(
    () => ({
      sortBy: args.sortBy
        ? {
            field: args.sortBy.column,
            direction: args.sortBy.sort
          }
        : undefined,
      amount: pageSize,
      offset: (page - 1) * pageSize,
      email: !!args.email ? args.email : undefined,
      companyId: !!args.companyId ? args.companyId : undefined,
      roles: args.roles?.length ? args.roles : undefined
    }),
    [args.sortBy, args.email, args.companyId, args.roles, pageSize, page]
  );

  const updateArgs = useCallback((changedArgs: Partial<AdminUsersVars>) => {
    updateQueryParams(changedArgs);
    setArgs(currentState => ({ ...currentState, ...changedArgs }));
  }, []);

  return useMemo(() => [{ args, vars, initArgs: defaultArgs }, updateArgs], [args, vars, defaultArgs, updateArgs]);
}

export function useAdminUsers(): UseAdminUsers {
  const [{ vars }] = useAdminUsersVars();
  const query = useQuery<ApiAdminUsersResponse, ApiAdminUsersVars>(GET_ALL_USERS, {
    variables: vars,
    skip: !vars,
    notifyOnNetworkStatusChange: true
  });
  return useMemo(
    () => ({
      ...query,
      data: query.data ? new AdminUsersData(query.data.searchUser) : undefined,
      loading:
        (query.loading || query.data === undefined || query.networkStatus === 4 || query.networkStatus === 2) &&
        query.networkStatus !== 3,
      fetchingMore: query.networkStatus === 3
    }),
    [query]
  );
}

export interface IActiveUsersInfo {
  availableUserCount: number;
  allUsersCSV: string;
}

export function useActiveUsersInfo(): QueryResult<{ getAdminUsersInfo: IActiveUsersInfo }> {
  return useQuery<{ getAdminUsersInfo: IActiveUsersInfo }>(GET_ACTIVE_USERS_INFO);
}
